import React from 'react'

import Grid from '@material-ui/core/Grid'
import { withStyles } from '@material-ui/core/styles'
import Tooltip from '@material-ui/core/Tooltip'
import DirectionsBikeIcon from '@material-ui/icons/DirectionsBike'
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar'
import DirectionsWalkIcon from '@material-ui/icons/DirectionsWalk'
import ToggleButton from '@material-ui/lab/ToggleButton'
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup'
import distance from '@turf/distance'
import { point } from '@turf/helpers'
import get from 'lodash/get'
import isNil from 'lodash/isNil'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import {
  LAYOUT_DRAWER_TABS_SEARCH_PANELS_DIRECTIONS_EXPANDED,
  LAYOUT_DRAWER_TABS_SEARCH_PANELS_DIRECTIONS_PATH_FINDER,
  LAYOUT_DRAWER_TABS_SEARCH_PANELS_DIRECTIONS_PATH_FINDER_SHORTEST,
  LAYOUT_DRAWER_TABS_SEARCH_PANELS_DIRECTIONS_TRAVEL_MODE,
  setDirectionsTravelMode
} from 'api/actions'
import * as PoziUtils from 'api/utils'
import ShowRoute from 'drawer/tabs/search/panels/ShowRoute'
import PathFinder from 'utils/geojson-path-finder'

const styles = theme => ({
  root: { paddingBottom: 10 },
  header: {},
  shadeIcon: {
    color: '#0000ff',
    height: 20
  },
  shadeRMITIconWrapper: {

  },
  shadeRMITIcon: {
    height: 24,
    marginTop: 10,
    marginLeft: 10,
    verticalAlign: 'top'
  }
})

class SearchPanelDirectionsOptions extends React.Component {
  constructor (props) {
    super(props)

    /* console.log('distance,accuTherma,thermaDecPer,multiFactor,distThermaInverse,cost,weight')
    for (var d = 1; d <= 3; d++) {
      for (var at = 0; at <= 2; at++) {
        this.testWeightFn(d, 50 * at)
      }
    } */

    const { travelMode, classes } = this.props
    this.pure = {}
    this.pure.handleChange = this.handleChange.bind(this)
    // https://developer.mapquest.com/documentation/open/directions-api/route/get/
    this.pure.toggleButtons = [
      <ToggleButton key={1} value="fastest">
        <Tooltip title={'Car directions'} disableTouchListener={true} disableFocusListener={true}>
          <DirectionsCarIcon />
        </Tooltip>
      </ToggleButton>,
      <ToggleButton key={2} value="bicycle">
        <Tooltip title={'Cycling directions'} disableTouchListener={true} disableFocusListener={true}>
          <DirectionsBikeIcon />
        </Tooltip>
      </ToggleButton>,
      <ToggleButton key={3} value="pedestrian">
        <Tooltip title={'Walking directions'} disableTouchListener={true} disableFocusListener={true}>
          <DirectionsWalkIcon />
        </Tooltip>
      </ToggleButton>,
      <ToggleButton key={4} value="shade">
        <Tooltip title={'Shade directions'} disableTouchListener={true} disableFocusListener={true}>
          <img className={classes.shadeIcon} src="img/the-noun-project/nature_walk.svg" alt="nature_walk"/>
        </Tooltip>
      </ToggleButton>
    ]
    this.handleChange(null, travelMode)
  }

  testWeightFn (d, accuTherma) {
    this.getWeight(d, accuTherma, true)
  }

  /**
   * getWeight() is the core function that determines which path to choose for shadeways routing.
   * The function is run once when the dataset is loaded.
   * Each network edge (road segment) is evaluated based on its accuTherma/shade percentage from 0 to 100.
   * The function calculates a weight/cost for each segment based on the accuTherma (shade) value.
   * A lower accuTherma value means less shade and so incurs higher weighting/cost for the segment.
   * A higher accuTherma value has more shade, and so incurs a lower weighting/cost for the segment.
   * Additionally a cost factor is required in order to apply a preference to how much cost to apply to shade vs distance.
   * That is, by adjusting the cost factor we adjust the cost to choosing a shady route over the shortest route.
   *
   * Here are some examples that show how the application uses the accuTherma and cost factor to determine the weight.
   *
   * distance,accuTherma,thermaDecPer,multiFactor,distThermaInverse,cost,weight
   * 1,0,0,5,1,5,6 <== cost(5) is factor(5) * distance(1); weight(6) === cost(5) + distance(1)
   * 1,50,0.5,5,0.5,2.5,3.5
   * 1,100,1,5,0,0,1 <== cost is 0 because therm is 100% so weight(1) === distance(1)
   *
   * 2,0,0,5,2,10,12 <== cost(10) is factor(5) * distance(2); weight(12) === cost(10) + distance(2)
   * 2,50,0.5,5,1,5,7
   * 2,100,1,5,0,0,2 <== cost is 0 because therm is 100% so weight(2) === distance(2)
   *
   * 3,0,0,5,3,15,18 <== cost is factor (5) so weight === distance (15) + factor (5)
   * 3,50,0.5,5,1.5,7.5,10.5
   * 3,100,1,5,0,0,3 <== cost is 0 because therm is 100% so weight(3) === distance(3)
   *
   * @param {decimal} dist Distance (any units)
   * @param {integer} accuTherma Thermal value 0 - 100 (0 - no shade, 100 - full shade)
   * @param {bool} bLog Output console log message
   */
  getWeight (dist, accuTherma, bLog) {
    // Ensure we have a valid accuTherma value from 0 to 100.
    if (accuTherma < 0) {
      accuTherma = 0
    } else if (accuTherma > 100) {
      accuTherma = 100
    }

    // A multiplying factor on the Therma weighting.
    const multiFactor = 5

    // Therma value expressed as a decimal percentage.
    // E.g. 30 = 0.3, 70 = 0.7
    const thermaDecPer = accuTherma / 100

    // A higher accuTherma (shade) value represents a more appealing path, so we actually need the inverse as a function of distance in order to apply a cost.
    // distThermaInverse is the inverse of the distance based on percentage of shade.
    // The values range from 0 to d
    // E.g:
    //   30% therma/shade: 1 - (1 * 0.3) = 0.7
    //   70% therma/shade: 1 - (1 * 0.7) = 0.3
    const distThermaInverse = dist - (dist * thermaDecPer)

    // We need to apply some multiplying factor to get the algorithm to more or less choose an overall shadier path.
    const cost = distThermaInverse * multiFactor

    // Add the cost to the distance.
    const weight = dist + cost

    // Output to the console for testing.
    if (bLog) {
      console.log(this.round(dist, 4) + ',' + accuTherma + ',' + this.round(thermaDecPer, 4) + ',' + multiFactor + ',' + this.round(distThermaInverse, 4) + ',' + this.round(cost, 4) + ',' + this.round(weight, 4))
    }

    // Return the final edge weighting/cost.
    return weight
  }

  /**
   * This runs once at setup to generate the topology/network path weighting.
   * 0 least shade - 100 most shade
   **/
  weightFn (a, b, props) {
    const d = distance(point(a), point(b))
    const accuTherma = props.AT || 0
    return this.getWeight(d, accuTherma, false)
  }

  round (n, d) {
    const df = d * 100
    return Math.round((n * df)) / df
  }

  async handleChange (event, travelMode) {
    const { setDirectionsTravelMode } = this.props
    const weightFn = this.weightFn.bind(this)
    if (travelMode === 'shade') {
      const { pathFinder } = this.props
      if (isNil(pathFinder)) {
        // Instead of loading the raw data, load the generated network graph.
        // const url = '/data/osm_thermal_veg.json'
        const url = 'https://data.pozi.com/bendigo/osm_thermal_veg_reduced.json'
        const options = {
          weightFn
        }

        /*
        const urlBendigoRoadGraphShade = 'https://data.pozi.com/bendigo/bendigo-road-graph-shade.json'
        const urlBendigoRoadGraphShortest = 'https://data.pozi.com/bendigo/bendigo-road-graph-shortest.json'

        const bendigoRoadGraphShadeResponse = await window.fetch(urlBendigoRoadGraphShade)

        if (isNil(bendigoRoadGraphShadeResponse) || bendigoRoadGraphShadeResponse.status !== 200) {
          return
        }
        var bendigoRoadGraphShadeData = await bendigoRoadGraphShadeResponse.json()
        const pathFinder = new PathFinder(bendigoRoadGraphShadeData, options)

        const bendigoRoadGraphShortestResponse = await window.fetch(urlBendigoRoadGraphShortest)
        if (isNil(bendigoRoadGraphShortestResponse) || bendigoRoadGraphShortestResponse.status !== 200) {
          return
        }
        var bendigoRoadGraphShortestData = await bendigoRoadGraphShortestResponse.json()
        const pathFinderShortest = new PathFinder(bendigoRoadGraphShortestData, {})

        */

        const osmThermalVegGeoJSONResponse = await window.fetch(url)
        if (isNil(osmThermalVegGeoJSONResponse) || osmThermalVegGeoJSONResponse.status !== 200) {
          return
        }
        const osmThermalVegGeoJSON = await osmThermalVegGeoJSONResponse.json()
        const pathFinderShade = new PathFinder(osmThermalVegGeoJSON, options)
        const pathFinderShortest = new PathFinder(osmThermalVegGeoJSON, {})

        PoziUtils.gtag({
          category: 'useraction',
          status: 'success',
          identifier: 'shadeways data',
          action: 'shadeways thermal data loaded'
        })

        setDirectionsTravelMode(travelMode, pathFinderShade, pathFinderShortest)
      } else {
        setDirectionsTravelMode(travelMode)
      }
    } else {
      setDirectionsTravelMode(travelMode)
    }
  }

  render () {
    const { classes, travelMode, expanded } = this.props

    if (!expanded) {
      return null
    }

    ShowRoute()

    let shadeLogos = null
    if (travelMode === 'shade') {
      shadeLogos = <span className={classes.shadeRMITIconWrapper}><img className={classes.shadeRMITIcon} src="img/custom/rmit-logo.png" alt="rmit logo"/></span>
    }

    return (
      <div className={classes.root}>
        <Grid container spacing={2} direction="column">
          <Grid item>
            <ToggleButtonGroup size="small" value={travelMode} exclusive onChange={this.pure.handleChange}>
              {this.pure.toggleButtons}
            </ToggleButtonGroup>
            {shadeLogos}
          </Grid>
        </Grid>
      </div>
    )
  }
}

SearchPanelDirectionsOptions.propTypes = {
  classes: PropTypes.object.isRequired,
  setDirectionsTravelMode: PropTypes.func.isRequired,
  travelMode: PropTypes.string.isRequired,
  expanded: PropTypes.bool.isRequired,
  pathFinder: PropTypes.object
}

function mapStateToProps (state, ownProps) {
  const travelMode = get(state.config, LAYOUT_DRAWER_TABS_SEARCH_PANELS_DIRECTIONS_TRAVEL_MODE, 'car')
  const pathFinder = get(state.config, LAYOUT_DRAWER_TABS_SEARCH_PANELS_DIRECTIONS_PATH_FINDER, null)
  const pathFinderShortest = get(state.config, LAYOUT_DRAWER_TABS_SEARCH_PANELS_DIRECTIONS_PATH_FINDER_SHORTEST, null)
  const expanded = get(state, 'config.' + LAYOUT_DRAWER_TABS_SEARCH_PANELS_DIRECTIONS_EXPANDED, true)
  return {
    travelMode,
    pathFinder,
    pathFinderShortest,
    expanded
  }
}

const mapDispatchToProps = dispatch => {
  return {
    setDirectionsTravelMode: (travelMode, pathFinder, pathFinderShortest) => {
      dispatch(setDirectionsTravelMode(travelMode, pathFinder, pathFinderShortest))
    }
  }
}

export default (
  withStyles(styles)(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(SearchPanelDirectionsOptions)
  )
)
